import { routes } from "@constants/routes";
import { Actions, ArrowRightIcon, FormGridContainer } from "@cpj-ai/panel-npm-package";
import { GetJobPositionByIdQuery } from "@graphql/generated";
import { useCallback, useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUpdateJobPosition } from "../useUpdateJobPosition";
import { getInputConfig } from "./formConfig";

interface ISalaryStepContentProps {
  jobPositionData?: NonNullable<NonNullable<GetJobPositionByIdQuery["organization"]>["jobPosition"]>["get"];
}

const SalaryStepContent = ({ jobPositionData }: ISalaryStepContentProps) => {
  const formHandler = useForm();
  const { handleSubmit, loading: submitLoading } = useUpdateJobPosition();
  const [searchParams] = useSearchParams();
  const inputConfig = getInputConfig({ formData: formHandler.getValues() });
  const navigate = useNavigate();

  const handleNext = useCallback(async () => {
    const id = searchParams.get("id");
    if (!id) {
      toast.error("No job post ID found");
      return;
    }
    navigate(routes.jobPositions.preview.replace(":id", id));
  }, [searchParams, navigate]);

  const handleClickSubmit = formHandler.handleSubmit(async (data: FieldValues) => {
    try {
      const id = searchParams.get("id");
      if (!id) return;
      await handleSubmit({
        id,
        handleNextStep: handleNext,
        values: {
          salaryRange: [+data.salaryMin, +data.salaryMax],
          ...data,
          benefits: data?.benefits?.map((item: { node: { id: string } }) => item?.node?.id),
        },
      });
    } catch (error) {
      console.error(error);
    }
  });

  formHandler.watch("salaryMin");
  formHandler.watch("salaryMax");
  formHandler.watch("workingStartAt");
  formHandler.watch("workingEndAt");

  useEffect(() => {
    if (jobPositionData) {
      formHandler.reset({
        contractType: jobPositionData.contractType,
        paymentTerm: jobPositionData.paymentTerm,
        workingStartAt: jobPositionData.workingStartAt,
        workingEndAt: jobPositionData.workingEndAt,
        benefits: jobPositionData.benefits?.map((benefit) => ({ node: benefit })),
        otherBenefits: jobPositionData.otherBenefits,
        daysOff: jobPositionData.daysOff,
        ...(jobPositionData.salaryRange && {
          salaryMin: String(jobPositionData.salaryRange[0]),
          salaryMax: String(jobPositionData.salaryRange[1]),
        }),
      });
    }
  }, [formHandler, jobPositionData]);

  return (
    <div className="mb-8">
      {/* <Typography className="font-bold">new Job Details :</Typography>
      <Typography className="text-xs mb-5 mt-3">
        In this section, define the basic information about the new job post, including the job title, description,
        number of vacancies, initial requirements, and the start date.
      </Typography> */}

      <FormGridContainer formHandler={formHandler} inputConfigs={inputConfig} />

      <Actions
        className="mt-6"
        showCancel={false}
        submitProps={{
          onClick: handleClickSubmit,
          isLoading: submitLoading,
          isDisabled: submitLoading,
          children: "Next",
          endContent: <ArrowRightIcon stroke="#fff" className="w-4 h-4" />,
        }}
      />
    </div>
  );
};

export default SalaryStepContent;
